import { Audio } from 'expo-av';
import { useEffect, useState } from 'react';

const SOUNDS = {
	crossing: require('../assets/sounds/crossing.mp3'),
	horn1: require('../assets/sounds/horn1.mp3'),
	horn2: require('../assets/sounds/horn2.mp3'),
	horn3: require('../assets/sounds/horn3.mp3'),
	run1: require('../assets/sounds/run1.mp3'),
	run2: require('../assets/sounds/run2.mp3'),
	stopping: require('../assets/sounds/stopping.mp3'),
} as const;

export type SoundType = keyof typeof SOUNDS;

type Sounds = {
	[key in SoundType]: Audio.Sound;
};

export type ScoreItem = SoundType | number;

export type SoundsUtil = {
	isReady: boolean;
	play: (soundType: SoundType) => Promise<void>;
	stop: (soundType: SoundType) => Promise<void>;
};

const useSounds = (): SoundsUtil => {
	const [isReady, setIsReady] = useState(false);
	const [sounds, setSounds] = useState<Partial<Sounds>>({});

	const _init = async () => {
		try {
			let _sounds = {};
			for (const [key, _soundSource] of Object.entries(SOUNDS)) {
				const _sound = new Audio.Sound();
				await _sound.loadAsync(_soundSource);
				_sounds = Object.assign({}, _sounds, {
					[key]: _sound,
				});
			}
			setSounds(_sounds);
			console.info('Sounds initialized successfully.');
		} catch (error) {
			console.error(error);
		}
	};

	const _destroy = async () => {
		try {
			for (const [, _sound] of Object.entries(sounds)) {
				await _sound?.stopAsync();
				await _sound?.unloadAsync();
			}
			setSounds({});
			console.info('Sounds destroyed successfully');
		} catch (error) {
			console.error(error);
		}
	};

	const play = async (soundType: SoundType) => {
		const _sound = sounds[soundType];
		try {
			await _sound?.setPositionAsync(0);
			await _sound?.playAsync();
		} catch (error) {
			console.error(error);
		}
	};

	const stop = async (soundType: SoundType) => {
		const _sound = sounds[soundType];
		try {
			await _sound?.stopAsync();
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		(async () => {
			await _init();
			setIsReady(true);
		})();
		return () => {
			(async () => {
				await _destroy();
				setIsReady(false);
			})();
		};
	}, []);

	return {
		isReady,
		play,
		stop,
	};
};

export default useSounds;
